import { useState } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { usePermissions } from "@/hooks/usePermissions";
import { useTheme } from "@/hooks/useTheme";
import {
  Car,
  House,
  Users,
  Receipt,
  UserCircle,
  GearSix,
  SignOut,
  CaretLeft,
  CaretRight,
  Sun,
  Moon,
  Wrench,
  Envelope,
  Truck,
  ShoppingCart,
  Handshake,
  ChartBar,
  ChatCircle,
  CalendarBlank,
  UploadSimple
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import MobileNav from "@/components/MobileNav";
import InstallPWA from "@/components/InstallPWA";
import { NotificationBell } from "@/components/NotificationBell";

const ROLE_LABELS = {
  admin: "Administrateur",
  commercial: "Commercial",
  preparateur: "Préparateur",
  auditeur: "Auditeur",
  comptabilite: "Comptabilité"
};

const Layout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const { user, logout } = useAuth();
  const { can } = usePermissions();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const navItems = [
    { path: "/dashboard", icon: House, label: "Tableau de bord", check: () => can("dashboard", "read") },
    { path: "/vehicules", icon: Car, label: "Véhicules", check: () => can("vehicles", "read") },
    { path: "/clients", icon: Users, label: "Clients", check: () => can("clients", "read") },
    { path: "/factures", icon: Receipt, label: "Factures", check: () => can("invoices", "read") },
    { path: "/factures-achat", icon: ShoppingCart, label: "Achats", check: () => can("purchase_invoices", "read") },
    { path: "/fournisseurs", icon: Truck, label: "Fournisseurs", check: () => can("fournisseurs", "read") },
    { path: "/depot-vente", icon: Handshake, label: "Depot-Vente", check: () => can("depot_vente", "read") },
    { path: "/emails", icon: Envelope, label: "Emails", check: () => can("emails", "read") },
    { path: "/rapports", icon: ChartBar, label: "Rapports", check: () => can("rapports", "read") },
    { path: "/chat", icon: ChatCircle, label: "Chat", check: () => true },
    { path: "/calendrier", icon: CalendarBlank, label: "Calendrier", check: () => true },
    { path: "/import-csv", icon: UploadSimple, label: "Import CSV", check: () => can("vehicles", "create") },
    { path: "/prestataires", icon: Wrench, label: "Prestataires", check: () => can("preparation", "read") },
    { path: "/utilisateurs", icon: UserCircle, label: "Utilisateurs", check: () => can("users", "read") },
    { path: "/parametres", icon: GearSix, label: "Paramètres", check: () => can("settings", "read") },
  ];

  const filteredNavItems = navItems.filter(item => item.check());

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex">
      <MobileNav />
      <InstallPWA />

      {/* Desktop Sidebar */}
      <aside
        data-testid="sidebar"
        className={`sidebar transition-all duration-300 ${collapsed ? "sidebar-collapsed" : ""}`}
      >
        {/* Logo */}
        <div className="sidebar-header">
          {!collapsed ? (
            <Link to="/dashboard" className="flex items-center gap-2.5">
              <Car size={26} weight="bold" className="text-blue-600" />
              <span className="font-extrabold text-lg tracking-tight text-slate-900">MikAuto</span>
              <span className="text-overline text-blue-600 ml-0.5">Belgium</span>
            </Link>
          ) : (
            <Car size={26} weight="bold" className="text-blue-600 mx-auto" />
          )}
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          {!collapsed && (
            <div className="sidebar-section">
              <p className="sidebar-section-title">Navigation</p>
            </div>
          )}
          <ul className="space-y-1">
            {filteredNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path ||
                (item.path !== "/dashboard" && location.pathname.startsWith(item.path));

              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    data-testid={`nav-${item.path.slice(1)}`}
                    className={`sidebar-nav-item ${isActive ? "active" : ""} ${collapsed ? "justify-center px-2" : ""}`}
                  >
                    <Icon size={20} weight={isActive ? "fill" : "regular"} />
                    {!collapsed && <span>{item.label}</span>}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          data-testid="toggle-sidebar"
          className="absolute -right-3 top-20 w-6 h-6 bg-white border border-slate-200 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-50 shadow-sm transition-colors z-50"
        >
          {collapsed ? <CaretRight size={12} /> : <CaretLeft size={12} />}
        </button>

        {/* User section */}
        <div className="sidebar-footer">
          <NotificationBell collapsed={collapsed} />
          <button
            onClick={toggleTheme}
            data-testid="theme-toggle"
            className={`flex items-center gap-3 w-full rounded-lg p-2 mb-2 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-slate-600 dark:text-slate-400 ${collapsed ? "justify-center" : ""}`}
          >
            {theme === "dark" ? <Sun size={20} /> : <Moon size={20} />}
            {!collapsed && <span className="text-sm font-medium">{theme === "dark" ? "Mode clair" : "Mode sombre"}</span>}
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="user-menu-trigger"
                className={`flex items-center gap-3 w-full rounded-lg p-2 hover:bg-slate-100 transition-colors ${collapsed ? "justify-center" : ""}`}
              >
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-blue-100 text-blue-700 text-sm font-semibold">
                    {user?.name?.charAt(0)?.toUpperCase() || "U"}
                  </AvatarFallback>
                </Avatar>
                {!collapsed && (
                  <div className="flex-1 text-left">
                    <p className="text-sm font-semibold text-slate-900 truncate">{user?.name}</p>
                    <p className="text-xs text-slate-500">{ROLE_LABELS[user?.role]}</p>
                  </div>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-1.5">
                <p className="text-sm font-semibold">{user?.name}</p>
                <p className="text-xs text-slate-500">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid="logout-btn"
                onClick={handleLogout}
                className="text-red-600 focus:text-red-600"
              >
                <SignOut className="mr-2 h-4 w-4" />
                Déconnexion
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main content */}
      <main className={`flex-1 transition-all duration-300 ${collapsed ? "lg:ml-[72px]" : "lg:ml-[260px]"}`}>
        <div className="pt-14 pb-20 lg:pt-0 lg:pb-0 p-4 md:p-6 lg:p-8 xl:p-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default Layout;
